import { useToast } from '@chakra-ui/react';
import { PrintTaskResponse } from '@zap-onboard/api-client';
import { Optional } from 'designed';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHandler, useHandlerQuery } from '.';
import { logError } from '../libs/errorLib';
import { track } from '../libs/track';
import { useAPI } from '../services/API';

const PRINTING_TOAST_ID = 'PRINTING_TASK';
export const usePrintTask = () => {
  const toast = useToast();
  const api = useAPI();

  const [taskId, setTaskId] = useState<string | null>(null);
  const [completed, setCompleted] = useState<boolean>(true);
  const [calls, setCalls] = useState<number>(0);

  const reset = useCallback(() => {
    setCalls(0);
    setCompleted(true);
    setTaskId(null);
  }, []);

  const startPrinting = useCallback((taskId: string) => {
    reset();
    setTaskId(taskId);
    setCompleted(false);
    track.event('Printed Task', { taskId });
    toast({
      id: PRINTING_TOAST_ID,
      title: 'Converting to PDF...',
      status: 'info',
      position: 'bottom-right',
      isClosable: false,
      duration: null,
    });
  }, []);

  const enabled = !!taskId && !completed;

  const printJob = useHandlerQuery(
    () =>
      api.flows().printTask({
        taskId: taskId!,
      }),
    {
      key: ['PRINT_TASK', taskId!],
      refetchEvery: enabled ? '3s' : undefined,
      skipRefetch: true,
      enabled,
      keepPreviousData: false,
      onSuccess: () => setCalls((c) => c + 1),
      retry: 1,
    },
  );

  const complete = printJob.data?.is('COMPLETE').orElse(undefined);
  const inProgress = printJob.data?.is('IN_PROGRESS').orElse(undefined);
  const error = useMemo(
    () =>
      printJob.data
        ?.is('FAILED')
        .map((failed) => new Error(failed.failureMessage))
        .orElse(undefined) || printJob.error,
    [printJob.data, printJob.error],
  );

  useEffect(() => {
    if (completed && toast.isActive(PRINTING_TOAST_ID)) {
      toast.close(PRINTING_TOAST_ID);
    }
  }, [completed]);

  useEffect(() => {
    if (completed) {
      return;
    }
    if (inProgress) {
      if (calls > 20) {
        setCompleted(true);
        toast({
          title: 'This is taking longer than expected',
          description:
            'The PDF conversion is taking longer than usual. Please try again later.',
          status: 'warning',
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } else if (complete) {
      setCompleted(true);
      window.open(complete.pdf.url, '_blank', 'noopener,noreferrer');
    } else if (error) {
      setCompleted(true);
      logError(error);
      toast({
        title: 'Could not convert this task to a PDF',
        description: error.message,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    }
  }, [completed, error, complete, inProgress]);

  return {
    handlePrintTask: (newTaskId: string) => {
      startPrinting(newTaskId);
    },
    isTaskPrinting: enabled,
  };
};
